import { ProductInventoryGrid, Tabs, TabsContent, TabsList, TabsTrigger } from "randmarcomps";
import React from "react";
import { Product } from "../../stores/product/Product";
import OpenOrdersGrid from "../grids/OpenOrdersGrid";
import OpenPurchaseOrdersGrid from "../grids/OpenPurchaseOrdersGrid";
import OpportunityDetailsGrid from "../grids/OpportunityDetailsGrid";
import ProductGeneral from "./ProductGeneral";

import withManufacturer from "../../contexts/WithManufacturer";

interface IProps {
  applicationId: string;
  product: Product;
  defaultTabIndex?: number;
}

const tabConfigs = [
  { value: "general", label: "General" },
  { value: "inventory", label: "Inventory" },
  { value: "purchases", label: "Purchases" },
  { value: "open-orders", label: "Open Orders" },
  { value: "completed-orders", label: "Completed Orders" },
  { value: "opportunities", label: "Opportunities" },
];

const ProductDescriptionTable: React.FC<IProps> = ({ product, defaultTabIndex }) => {
  const [activeTab, setActiveTab] = React.useState(tabConfigs[defaultTabIndex || 0].value);

  const sortedPurchaseOrders = React.useMemo(
    () => (((product as any).purchaseOrdersPartNumbers || (product as any).PurchaseOrders) || []).slice().sort((a: any, b: any) => ((a.purchaseOrderNumber || a.PurchaseOrderNumber) || "").localeCompare((b.purchaseOrderNumber || b.PurchaseOrderNumber) || "")),
    [product]
  );

  const sortedRequisitions = React.useMemo(
    () => (((product as any).requisitionsPartNumbers || (product as any).Requisitions) || []).slice().sort((a: any, b: any) => ((a.requisitionNumber || a.RequisitionNumber) || "").localeCompare((b.requisitionNumber || b.RequisitionNumber) || "")),
    [product]
  );

  const hasPurchases = React.useMemo(() => ((((product as any).purchaseOrdersPartNumbers || (product as any).PurchaseOrders) || []).length > 0 || (((product as any).requisitionsPartNumbers || (product as any).Requisitions) || []).length > 0), [product]);
  const hasOpenOrders = React.useMemo(() => (((product as any).activeOrderDetails || (product as any).ActiveOrderDetails) || []).length > 0, [product]);
  const hasCompletedOrders = React.useMemo(() => (((product as any).completedOrderDetails || (product as any).CompletedOrderDetails) || []).length > 0, [product]);
  const hasOpportunities = React.useMemo(() => (((product as any).opportunities || (product as any).Opportunities) || []).length > 0, [product]);

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <div className="overflow-x-auto bg-slate-50 rounded-lg p-1">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="purchases" disabled={!hasPurchases}>Purchases</TabsTrigger>
          <TabsTrigger value="open-orders" disabled={!hasOpenOrders}>Open Orders</TabsTrigger>
          <TabsTrigger value="completed-orders" disabled={!hasCompletedOrders}>Completed Orders</TabsTrigger>
          <TabsTrigger value="opportunities" disabled={!hasOpportunities}>Opportunities</TabsTrigger>
        </TabsList>
      </div>

      <div className="mt-2.5">
        <TabsContent value="general">
          <ProductGeneral product={product} />
        </TabsContent>
        <TabsContent value="inventory">
          {
            ((product as any).distribution || (product as any).Distribution) &&
            <ProductInventoryGrid inventory={((product as any).distribution || (product as any).Distribution).Inventory!.map((wi: any) => ({
              Name: wi.Name!,
              WarehouseId: wi.WarehouseId!,
              AvailableQuantity: wi.AvailableQuantity!,
              Province: wi.Province!,
              Country: wi.Country!,
              PurchaseOrderQuantity: wi.PurchaseOrderQuantity!,
              BinLocation: wi.BinLocation!,
            }))} />
          }
        </TabsContent>
        <TabsContent value="purchases">
          <OpenPurchaseOrdersGrid purchaseOrdersPartNumbers={sortedPurchaseOrders} requisitionsPartNumbers={sortedRequisitions} />
        </TabsContent>
        <TabsContent value="open-orders">
          <OpenOrdersGrid productOrders={((product as any).activeOrderDetails || (product as any).ActiveOrderDetails)} />
        </TabsContent>
        <TabsContent value="completed-orders">
          <OpenOrdersGrid productOrders={((product as any).completedOrderDetails || (product as any).CompletedOrderDetails)} defaultRowsPerPage={10} withSort />
        </TabsContent>
        <TabsContent value="opportunities">
          <OpportunityDetailsGrid productOpportunities={((product as any).opportunities || (product as any).Opportunities)} product={product} />
        </TabsContent>
      </div>
    </Tabs>
  );
}

export default withManufacturer(ProductDescriptionTable);
